/**
 * Shared visual helpers for ticket cards.
 *
 * Centralised so the draft and submitted cards use the same colour
 * vocabulary for ticket type and severity.
 */

interface AccentColors {
  fg: string
  bg: string
  border: string
}

/** Format a ticket type enum into a display label. */
export function formatTicketType(type: string): string {
  switch (type) {
    case 'bug':
      return 'Bug'
    case 'feature_request':
      return 'Feature Request'
    case 'improvement':
      return 'Improvement'
    case 'task':
      return 'Task'
    case 'maintenance':
      return 'Maintenance'
    case 'analysis':
      return 'Analysis'
    default:
      return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }
}

/** Accent colours for each ticket type — bugs are warning-tinted, features are accent-tinted. */
export function ticketTypeAccent(type: string): AccentColors {
  switch (type) {
    case 'bug':
      // Soft red — distinct from severity colours so they don't clash
      return {
        fg: '#fb7185',
        bg: 'rgba(251, 113, 133, 0.10)',
        border: 'rgba(251, 113, 133, 0.30)',
      }
    case 'feature_request':
      return {
        fg: '#60a5fa',
        bg: 'rgba(96, 165, 250, 0.10)',
        border: 'rgba(96, 165, 250, 0.30)',
      }
    case 'improvement':
      return {
        fg: '#a78bfa',
        bg: 'rgba(167, 139, 250, 0.10)',
        border: 'rgba(167, 139, 250, 0.30)',
      }
    default:
      return {
        fg: 'var(--text-secondary)',
        bg: 'var(--bg-tertiary)',
        border: 'var(--border-subtle)',
      }
  }
}

/** Format a severity enum into a display label. */
export function formatSeverityLabel(severity: string): string {
  if (!severity) return ''
  const lower = severity.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1) + ' Severity'
}

/** Accent colours for each severity level. */
export function severityAccent(severity: string): AccentColors {
  switch (severity) {
    case 'critical':
      return {
        fg: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: 'rgba(239, 68, 68, 0.35)',
      }
    case 'high':
      return {
        fg: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.12)',
        border: 'rgba(245, 158, 11, 0.35)',
      }
    case 'medium':
      return {
        fg: '#facc15',
        bg: 'rgba(250, 204, 21, 0.10)',
        border: 'rgba(250, 204, 21, 0.30)',
      }
    case 'low':
      return {
        fg: 'var(--text-muted)',
        bg: 'var(--bg-tertiary)',
        border: 'var(--border-subtle)',
      }
    default:
      return {
        fg: 'var(--text-muted)',
        bg: 'var(--bg-tertiary)',
        border: 'var(--border-subtle)',
      }
  }
}
